<form [formGroup]="form.formGroup" class="h-full min-h-0 flex flex-col">

  <p-toolbar>
    <ng-template #start>
      <div class="flex space-x-4 p-1">
        <div class="flex">
          <p-toggleswitch
            [formControlName]="form.formControlNames.co2withImpurities"
            (onChange)="typeChange(); getPercentages()" inputId="hasImpurities">
          </p-toggleswitch>
          <label class="ml-2" for="hasImpurities">CO₂ With Impurities</label>
        </div>

        <div class="flex">
          <p-toggleswitch inputId="phaseEnvelope" [disabled]="hideGergCalc" (onChange)="tablePlotToggle()"></p-toggleswitch>
          <label for="phaseEnvelope" class="ml-2">Phase Envelope</label>
        </div>

        @if (showPlot) {
          <div class="flex">
            <p-toggleswitch inputId="showGrid" [disabled]="isReadOnly" (onChange)="plotTableResultChange()"></p-toggleswitch>
            <label class="ml-2" for="showGrid">Show Grid</label>
          </div>
        }
      </div>
    </ng-template>

    <ng-template #end>
      <div class="flex">
        <svg-icon
          src="../../assets/icons/reset-default-24x24.svg"
          class="w-[25px] h-[25px] tree-icon cursor-pointer"
          (click)="applyDefaults()"
          title="Apply Defaults">
        </svg-icon>
      </div>
    </ng-template>
  </p-toolbar>

  @if (showPlot || showGridResult) {
    @if (showGridResult) {
      <app-ng-table-results
        tableName="Phase Envelope"
        [tableData]="resultsGrid"
        [columnDefinitions]="cols"
        [autoHeight]="true"
      />
    } @else if (!isLoading()) {
      <app-xy-line-plot
        [plotData]="plot.data" [xAxisTitle]="xAxisTitle" [yAxisTitle]="yAxisTitle" [plotName]="plotName"
        [downloadPlotName]="downloadPlotName" [reverseAutoRange]="true" [plotTitle]="'Fluids'"
        [xyLinePlotStore]="fluidsStore"
      ></app-xy-line-plot>
    }
  }

  <div class="mt-2" [hidden]="showPlot">
    <div class="mb-3">
      @if (!form.formGroup.controls.co2withImpurities.value) {
        <label class="text-sm">Pure CO₂ will be modelled</label>
      } @else {
        <label class="text-sm">CO₂ will be modelled with the following impurities:</label>
      }
    </div>
    <p-fieldset legend="Gas Composition %"
      [ngClass]="{ 'opacity-50 pointer-events-none': !form.formGroup.controls.co2withImpurities.value }">

      <div class="flex flex-wrap">

        <!-- Column 1 -->
        <div class="flex flex-col w-1/3 p-2 space-y-2">

          <div class="flex flex-col">
            <label>A<span class="text-sm align-sub">r</span></label>
            <app-validated-number-input
              [id]="'ar'"
              [control]="control('ar')"
              [value]="control('ar').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('ar', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>C1 =&nbsp;<span>CH</span><span class="text-sm align-sub">4</span></label>
            <app-validated-number-input
              [id]="'cH4'"
              [control]="control('cH4')"
              [value]="control('cH4').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('cH4', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>CO</label>
            <app-validated-number-input
              [id]="'cO'"
              [control]="control('cO')"
              [value]="control('cO').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('cO', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>H<span class="text-sm align-sub">2</span></label>
            <app-validated-number-input
              [id]="'h2'"
              [control]="control('h2')"
              [value]="control('h2').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('h2', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>NC<span class="text-sm align-sub">4</span></label>
            <app-validated-number-input
              [id]="'nC4'"
              [control]="control('nC4')"
              [value]="control('nC4').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('nC4', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>IC<span class="text-sm align-sub">5</span></label>
            <app-validated-number-input
              [id]="'iC5'"
              [control]="control('iC5')"
              [value]="control('iC5').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('iC5', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>C<span class="text-sm align-sub">8</span></label>
            <app-validated-number-input
              [id]="'c8'"
              [control]="control('c8')"
              [value]="control('c8').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('c8', $event)"
            ></app-validated-number-input>
          </div>

        </div>

        <!-- Column 2 -->
        <div class="w-full md:w-1/3 p-2 space-y-2">

          <div class="flex flex-col">
            <label>H<span class="text-sm align-sub">2</span>S</label>
            <app-validated-number-input
              [id]="'h2s'"
              [control]="control('h2s')"
              [value]="control('h2s').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('h2s', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>C<span class="text-sm align-sub">2</span></label>
            <app-validated-number-input
              [id]="'c2'"
              [control]="control('c2')"
              [value]="control('c2').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('c2', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>NO<span class="text-sm align-sub">2</span></label>
            <app-validated-number-input
              [id]="'nO2'"
              [control]="control('nO2')"
              [value]="control('nO2').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('nO2', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>O<span class="text-sm align-sub">2</span></label>
            <app-validated-number-input
              [id]="'o2'"
              [control]="control('o2')"
              [value]="control('o2').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('o2', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>i-C<span class="text-sm align-sub">4</span></label>
            <app-validated-number-input
              [id]="'iC4'"
              [control]="control('iC4')"
              [value]="control('iC4').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('iC4', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>NC<span class="text-sm align-sub">6</span></label>
            <app-validated-number-input
              [id]="'nC6'"
              [control]="control('nC6')"
              [value]="control('nC6').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('nC6', $event)"
            ></app-validated-number-input>
          </div>

        </div>

        <!-- Column 3 -->
        <div class="w-full md:w-1/3 p-2 space-y-2">

          <div class="flex flex-col">
            <label>N<span class="text-sm align-sub">2</span></label>
            <app-validated-number-input
              [id]="'n2'"
              [control]="control('n2')"
              [value]="control('n2').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('n2', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>C<span class="text-sm align-sub">3</span></label>
            <app-validated-number-input
              [id]="'c3'"
              [control]="control('c3')"
              [value]="control('c3').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('c3', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>H<span class="text-sm align-sub">2</span>O</label>
            <app-validated-number-input
              [id]="'h2O'"
              [control]="control('h2O')"
              [value]="control('h2O').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('h2O', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>S<span>O</span><span class="text-sm align-sub">2</span></label>
            <app-validated-number-input
              [id]="'sO2'"
              [control]="control('sO2')"
              [value]="control('sO2').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('sO2', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>NC<span>O</span><span class="text-sm align-sub">5</span></label>
            <app-validated-number-input
              [id]="'nC5'"
              [control]="control('nC5')"
              [value]="control('nC5').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('nC5', $event)"
            ></app-validated-number-input>
          </div>

          <div class="flex flex-col">
            <label>C<span class="text-sm align-sub">7</span></label>
            <app-validated-number-input
              [id]="'c7'"
              [control]="control('c7')"
              [value]="control('c7').value"
              [validation]="{ min: 0, max: 100 }"
              [unit]="'%'"
              [unitPrecisionOverrides]="{ '%': 6 }"
              (onChange)="validatePercentage('c7', $event)"
            ></app-validated-number-input>
          </div>

        </div>

      </div>
  
      <div class="flex flex-col mt-2 space-y-2">
        <label class="{{ totalError ? 'validation-error' : '' }}">Total Impurities: {{ totalPercentage }} %</label>

        <label>Total CO₂: {{ co2Percentage }} %</label>
        @if (gergLabel && !isLoading()) {
          <label class="validation-error">{{ gergLabel }}</label>
        }
      </div>

    </p-fieldset>
    <br>

  </div>
</form>
